import { useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useVirtualizer } from '@tanstack/react-virtual';
import { Plus } from 'lucide-react';
import type { Board } from '@/lib/api/types';
import { useAddAssetsToBoard, useBulkAction } from '@/lib/api/queries';
import { useSessionStore } from '@/stores/session';
import { toast } from '@/stores/ui';
import { SUBBOARD_VIRTUAL_THRESHOLD } from '@/config/constants';
import { Menu, type MenuItem } from '@/components/Menu';
import { usePermission } from '@/features/workspace/usePermission';
import { uploadManager } from '@/features/upload/UploadManager';
import type { AssetDragPayload } from '@/features/grid/dnd';
import { BoardCard } from './BoardCard';
import { CreateBoardDialog, DeleteBoardDialog, MoveBoardDialog, RenameBoardDialog } from './BoardDialogs';

/** §2.13 — flex-wrap of BoardCards; virtual single row when > 60. */
export function SubBoardRow({ parent, boards }: { parent: Board; boards: Board[] }) {
  const { ws = '' } = useParams();
  const wsId = useSessionStore((s) => s.workspace?.id ?? '');
  const addAssets = useAddAssetsToBoard();
  const bulk = useBulkAction(wsId);
  const canManage = usePermission('MANAGE_BOARD', { boardId: parent.id });
  const [menu, setMenu] = useState<{ x: number; y: number; board: Board } | null>(null);
  const [dialog, setDialog] = useState<{ kind: 'create' | 'rename' | 'move' | 'delete'; board: Board } | null>(null);

  const onDropAssets = (target: Board, p: AssetDragPayload, copy: boolean) => {
    if (p.source_board_id === target.id) return;
    if (copy || !p.source_board_id) addAssets.mutate({ boardId: target.id, assetIds: p.ids, fromBoardId: p.source_board_id, mode: 'copy' }, { onSuccess: () => toast({ kind: 'success', title: `Copied ${p.ids.length} to ${target.name}` }) });
    else bulk.mutate({ asset_ids: p.ids, action: 'move_to_board', params: { board_id: target.id, from_board_id: p.source_board_id } }, { onSuccess: () => toast({ kind: 'success', title: `Moved ${p.ids.length} to ${target.name}` }) });
  };
  const onDropFiles = (target: Board, dt: DataTransfer) => void uploadManager.enqueueDataTransfer(dt, target.id);
  const items: MenuItem[] = menu
    ? [
        { label: 'Rename / colour', onSelect: () => setDialog({ kind: 'rename', board: menu.board }) },
        { label: 'New sub-board', icon: <Plus size={14} />, onSelect: () => setDialog({ kind: 'create', board: menu.board }) },
        { label: 'Move…', onSelect: () => setDialog({ kind: 'move', board: menu.board }) },
        { kind: 'separator' },
        { label: 'Delete', danger: true, onSelect: () => setDialog({ kind: 'delete', board: menu.board }) },
      ]
    : [];

  const scrollRef = useRef<HTMLDivElement>(null);
  const virtual = boards.length > SUBBOARD_VIRTUAL_THRESHOLD;
  const v = useVirtualizer({ count: virtual ? boards.length : 0, horizontal: true, getScrollElement: () => scrollRef.current, estimateSize: () => 184, overscan: 4 });

  if (boards.length === 0 && !canManage) return null;
  return (
    <section aria-label="Sub-boards" className="mb-4">
      {virtual ? (
        <div ref={scrollRef} className="scrollbar-thin overflow-x-auto">
          <div style={{ width: v.getTotalSize(), height: 180, position: 'relative' }}>
            {v.getVirtualItems().map((vi) => {
              const b = boards[vi.index]!;
              return (
                <div key={b.id} style={{ position: 'absolute', left: vi.start, top: 0 }}>
                  <BoardCard board={b} to={`/w/${ws}/b/${b.id}`} onDropAssets={onDropAssets} onDropFiles={onDropFiles} onContextMenu={canManage ? (e, bd) => (e.preventDefault(), setMenu({ x: e.clientX, y: e.clientY, board: bd })) : undefined} />
                </div>
              );
            })}
          </div>
        </div>
      ) : (
        <div className="flex flex-wrap gap-3">
          {boards.map((b) => (
            <BoardCard key={b.id} board={b} to={`/w/${ws}/b/${b.id}`} onDropAssets={onDropAssets} onDropFiles={onDropFiles} onContextMenu={canManage ? (e, bd) => (e.preventDefault(), setMenu({ x: e.clientX, y: e.clientY, board: bd })) : undefined} />
          ))}
          {canManage && (
            <button type="button" onClick={() => setDialog({ kind: 'create', board: parent })} className="flex h-[168px] w-44 flex-col items-center justify-center gap-1 rounded-lg border border-dashed border-line-strong text-sm text-fg-muted hover:border-accent hover:text-accent">
              <Plus size={18} /> New board
            </button>
          )}
        </div>
      )}
      {menu && <Menu anchor={{ x: menu.x, y: menu.y }} items={items} onClose={() => setMenu(null)} />}
      {dialog?.kind === 'create' && <CreateBoardDialog parent={dialog.board} libraryId={dialog.board.library_id} onClose={() => setDialog(null)} />}
      {dialog?.kind === 'rename' && <RenameBoardDialog board={dialog.board} onClose={() => setDialog(null)} />}
      {dialog?.kind === 'move' && <MoveBoardDialog board={dialog.board} onClose={() => setDialog(null)} />}
      {dialog?.kind === 'delete' && <DeleteBoardDialog board={dialog.board} onClose={() => setDialog(null)} />}
    </section>
  );
}
